import React from "react";
import Styles from "./index.module.css";
import { companyProfile, location } from "../../../api";
import { toast } from "react-toastify";
import { useState, useEffect } from "react";
import ModalChangePlant from "./modal/changePlant";
import ConfirmationDlg from "../../../components/ConfirmationDlg";
import { useAuth } from "../../../contexts/AuthContext.jsx";
import { Card, CardBody, CardHeader } from "../../../components/card/card";
import ImageViewer from "../../../components/ImageViewer";
import { PermissionChecker } from "../../../components/PermissionChecker";
import {
  renderImage,
  printLocation,
  generateParentLocationFullPath,
} from "../../../utils/helper.jsx";
import { useDashboardContext } from "../../../contexts/NewDashboardContext.jsx";
import SearchableFilter from "../../../components/SearchableFilter/index.jsx";
import DataTablePagination from "../../../components/DataTablePagination/index.jsx";
import DebounceInput from "../../../components/form/HorizontalDebounceInput/index.jsx";

function groupLocationsByLevel(locations) {
  const grouped = [];

  function traverse(nodes, level = 0, parent_name = "") {
    if (!nodes || nodes.length === 0) return;

    // Ensure this level array exists
    if (!grouped[level]) grouped[level] = [];

    for (const node of nodes) {
      // Add current node to its level group
      node.full_path = parent_name
        ? parent_name + " - " + node.location_name
        : node.location_name;
      grouped[level].push(node);

      // Recursively process sublocations
      traverse(node.sublocations, level + 1, node.full_path);
    }
  }

  traverse(locations);
  return grouped;
}

// Example usage:
const locations = [
  /* your data here */
];
const locationGroupByLevel = groupLocationsByLevel(locations);

function LocationTab({ selectedIndex, onGrouped }) {
  const [openModal, setOpenModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selected, setSelected] = useState(false);
  const [filteredCompany, setFilteredCompany] = useState(null);
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [parentLocations, setParentLocations] = useState([]);
  const [selectedParentLocation, setSelectedParentLocation] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [deleteRow, setDeleteRow] = useState(null);
  const [confirmDlg, setConfirmDlg] = useState(false);
  const { updateLocations } = useDashboardContext();
  const { config } = useAuth();
  const location_wording = config.location_wording.split(",");
  const [query, setQuery] = useState({
    take: 12,
    page: 1,
    keyword: "",
  });
  const [paginationTotalRows, setPaginationTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const clickAdd = () => {
    setOpenModal(true);
    setEditMode(false);
    setSelected(null);
  };

  async function getLocationByLevel(
    filteredParentLocation,
    level,
    queryPagination = null
  ) {
    if (filteredParentLocation) {
      level = "";
    }
    const paginationQuery = queryPagination
      ? new URLSearchParams(queryPagination).toString()
      : "";

    try {
      setLoading(true);
      const res = await location.list(
        "",
        true,
        filteredParentLocation || "root",
        level,
        paginationQuery
      );
      if (res.ok) {
        const body = await res.json();
        let data = body.data.results;

        if (data.length) {
          setLocations(data);
          setPaginationTotalRows(body.data.totalData);
          if (queryPagination) {
            setQuery((prev) => ({
              ...prev,
              page: queryPagination.page,
              take: queryPagination.take,
            }));
          }
        } else {
          setLocations([]);
          setPaginationTotalRows(0);
        }
      }
    } catch (e) {
      toast.error(e.message);
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function getLocationDropdown(level) {
    try {
      const res = await location.list("", true, "nested_root", level);
      if (res.ok) {
        const body = await res.json();
        let data = body.data;

        if (data.length) {
          data.forEach((item) => {
            item.full_path = generateParentLocationFullPath(item);
          });
          setParentLocations(
            data.map((i) => {
              return {
                value: i.id,
                label: i.full_path,
              };
            })
          );
        } else {
          setParentLocations([]);
        }
      }
    } catch (e) {
      toast.error(e.message);
      console.error(e);
    }
  }

  const onChangeParentLocation = async (e) => {
    setSelectedParentLocation(e);
    await getLocationByLevel(e?.value, selectedIndex, { ...query, page: 1 });
  };

  const handleChangePage = (page) => {
    getLocationByLevel(selectedParentLocation?.value, selectedIndex, {
      ...query,
      page: page,
    });
  };

  const handleChangeRowsPerPage = (row) => {
    getLocationByLevel(selectedParentLocation?.value, selectedIndex, {
      ...query,
      page: 1,
      take: row,
    });
  };

  const handleChangeKeyword = async (e) => {
    await getLocationByLevel(selectedParentLocation?.value, selectedIndex, {
      ...query,
      keyword: e.target.value,
      page: 1,
    });
  };

  // async function getLocation(filteredParentLocation) {
  //   try {
  //     const res = await location.list("", true);
  //     if (res.ok) {
  //       const body = await res.json();
  //       let data = body.data;
  //       updateLocations(data);
  //       setLocations([]);
  //       if (data.length) {
  //         const grouped = groupLocationsByLevel(data);
  //         onGrouped(grouped);

  //         if (selectedIndex > 0) {
  //           setLocations(grouped[selectedIndex] || []);
  //           setParentLocations(grouped[selectedIndex - 1] || []);

  //           if (filteredParentLocation) {
  //             setFilteredLocations(
  //               (grouped[selectedIndex] || []).filter(
  //                 (i) => i.parent_location_id === filteredParentLocation
  //               )
  //             );
  //           } else {
  //             setFilteredLocations(grouped[selectedIndex] || []);
  //           }
  //         } else {
  //           setLocations(grouped[selectedIndex] || []);
  //           setFilteredLocations(grouped[selectedIndex] || []);
  //         }
  //       }
  //     }
  //   } catch (e) {
  //     toast.error(e.message);
  //     console.error(e);
  //   }
  // }

  useEffect(() => {
    getLocationByLevel(null, selectedIndex, { ...query, page: 1, keyword: "" });
  }, [selectedIndex]);

  useEffect(() => {
    if (selectedIndex > 0) {
      getLocationDropdown(selectedIndex - 1);
      setSelectedParentLocation(null);
    }
  }, [selectedIndex]);

  const onSubmitted = () => {
    getLocationByLevel(selectedParentLocation?.value, selectedIndex, {
      ...query,
      page: 1,
    });
  };
  function row(name, value) {
    return (
      <tr>
        <td style={{ fontSize: "12px", width: "100px" }}>{name}</td>
        <td style={{ width: "5px", textAlign: "center", fontSize: "12px" }}>
          :
        </td>
        <td className={"text-content-table"} style={{ fontSize: 12 }}>
          {value}
        </td>
      </tr>
    );
  }
  const editProfile = (row) => {
    setSelected(row);
    setOpenModal(true);
    setEditMode(true);
  };
  function showConfirmationDlg(row) {
    setDeleteRow(row);
    setConfirmDlg(true);
  }

  function hideConfirmationDialog() {
    setDeleteRow(null);
    setConfirmDlg(false);
  }

  async function deleteLocation() {
    try {
      const response = await location.delete(deleteRow.id);
      if (response.ok) {
        const body = await response.json();

        if (body.success) {
          toast.success(
            `This ${location_wording[selectedIndex]} has been deleted`
          );
          setConfirmDlg(false);
          setDeleteRow(null);
        } else {
          toast.error(body.message);
        }

        getLocationByLevel(selectedParentLocation?.value, selectedIndex, {
          ...query,
          page: query.page,
        });
      }
    } catch (e) {
      toast.error(e.message);
      console.error(e);
    }
  }

  return (
    <>
      <div p={5} className="widget" style={{ height: "auto" }}>
        <div className="widget-wrapper" />
        <ModalChangePlant
          selected={selected}
          openModal={openModal}
          setOpenModal={setOpenModal}
          editMode={editMode}
          title={
            (editMode ? "Edit" : "Add") + ` ${location_wording[selectedIndex]}`
          }
          onSubmitted={onSubmitted}
          selectedIndex={selectedIndex}
          parentLocations={parentLocations}
        />
        <div style={{ position: "relative" }}>
          <div className="row mb-4">
            <div className={"col-sm-12"}>
              <div
                style={{
                  width: "auto",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <div style={{ width: "30%" }}>
                  <DebounceInput
                    mb0={true}
                    name="keyword"
                    placeholder={"Search location"}
                    showDebounceIndicator
                    delay={1000}
                    onchange={handleChangeKeyword}
                  />
                </div>
                {selectedIndex != 0 && (
                  <div
                    style={{
                      zIndex: 11,
                      position: "relative",
                      marginLeft: "10px",
                      width: "30%",
                      flexGrow: 1,
                    }}
                  >
                    <SearchableFilter
                      value={selectedParentLocation}
                      label={location_wording[selectedIndex - 1]}
                      onChange={(e) => {
                        onChangeParentLocation(e);
                      }}
                      list={parentLocations}
                    />
                  </div>
                )}
                <PermissionChecker
                  permissions={["create location"]}
                  hideIfNotAllowed
                >
                  <button
                    className="btn btn-outline-theme"
                    style={{
                      marginLeft: "10px",
                      flexGrow: 1,
                      width: "30%",
                    }}
                    onClick={clickAdd}
                  >
                    Add {location_wording[selectedIndex]}
                  </button>
                </PermissionChecker>
              </div>
            </div>
          </div>

          <div mt={2} style={{ position: "relative" }}>
            {loading && (
              <div
                className="d-flex justify-content-center align-items-center position-absolute top-0 start-0 w-100 h-100"
                style={{
                  height: "100%",
                  width: "100%",
                  backgroundColor: "rgba(220, 219, 219, 0.5)",
                  zIndex: 1000,
                }}
              >
                <div className="spinner-grow text-theme"></div>
              </div>
            )}

            <div className={"row"}>
              {locations.map((i, index) => (
                <div key={i.id} className="col-sm-4">
                  <div>
                    <Card className="bg-theme border-theme bg-opacity-25 mb-3">
                      <CardHeader className="border-theme fw-bold small text-white text-center">
                        {printLocation(i.location_name)}
                      </CardHeader>
                      <CardBody>
                        <div>
                          {/* <div
                            className="card-fe"
                            style={{
                              // backgroundImage: i.picture_url,
                              margin: "0 -11px",
                            }}
                          ></div> */}

                          <ImageViewer
                            src={i.picture_url}
                            style={{ width: "50px" }}
                          />
                        </div>
                        <div
                          style={{
                            textAlign: "left",

                            padding: "10px",
                          }}
                        >
                          <div>
                            <table
                              style={{ textAlign: "left" }}
                              className="tblRole"
                            >
                              <tbody>
                                {row("Address", i.location_address)}
                                {row("Contact Person", i.contact_person)}
                                {row("Contact Number", i.contact_number)}
                                {row("Email", i.contact_email)}
                              </tbody>
                            </table>
                          </div>
                          <div className="row mt-3 justify-content-center">
                            <div className="col-sm-6">
                              <PermissionChecker
                                permissions={["edit location"]}
                                hideIfNotAllowed
                              >
                                <button
                                  onClick={() => {
                                    editProfile(i);
                                  }}
                                  className="btn btn-outline-light btn-sm w-100"
                                >
                                  Edit
                                </button>
                              </PermissionChecker>
                            </div>
                            <div className="col-sm-5">
                              <PermissionChecker
                                permissions={["delete location"]}
                                hideIfNotAllowed
                              >
                                <button
                                  onClick={() => {
                                    showConfirmationDlg(i);
                                  }}
                                  className="btn btn-outline-danger btn-sm w-100"
                                >
                                  Delete
                                </button>
                              </PermissionChecker>
                            </div>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <DataTablePagination
            scrollToTop={true}
            rowsPerPage={query.take}
            currentPage={query.page}
            totalRows={paginationTotalRows}
            onChangePage={(page) => handleChangePage(page)}
            onChangeRowsPerPage={(row) => handleChangeRowsPerPage(row)}
            rowsPerPageOptions={[12, 24, 48, 96]}
          />
        </div>
      </div>
      {deleteRow && (
        <ConfirmationDlg
          title={`Delete ${location_wording[selectedIndex]}`}
          open={confirmDlg}
          onClose={hideConfirmationDialog}
          onSubmit={deleteLocation}
        >
          <div style={{ pb: 2 }}>
            Are you sure you want to delete this{" "}
            {location_wording[selectedIndex]} <strong>{deleteRow.name}</strong>{" "}
            ?
          </div>
        </ConfirmationDlg>
      )}
    </>
  );
}

export default LocationTab;
